import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import * as utils from "common/utils";
import TileSubPanel from "components/TileSubPanel";
import { useSelector, useDispatch } from "react-redux";
import { updateBudget } from "../../Budget/_actions";
import { confirm } from "containers/App/_actions";
import ActionLink from "components/ActionLink";
import { useSoilModal } from "containers/Soils/SoilModal";
import { isSoilModified } from "containers/Soils/_utils";

export default function SoilOverview({ farm, budget }) {
    const [expandedSoilIds, setExpandedSoilIds] = useState([]);
    const refData = useSelector((state) => state.app.refData);
    const dispatch = useDispatch();
    const [openSoilModal, soilModal] = useSoilModal();
    const { blocks = [], farmSoilBlocks = [] } = budget;
    const soils = budget.useFarmSoils ? farm.soils : budget.soils;
    const removeLinks = budget.useFarmSoils;

    const productiveBlocks = budget.blocks.filter((b) => b.isProductive && b.type !== "FodderCrop");
    let totalProductiveArea = productiveBlocks.reduce((total, block) => (total += isNaN(block.areaInHectares) ? 0 : block.areaInHectares), 0);

    //avoid % by 0
    totalProductiveArea = totalProductiveArea > 0 ? totalProductiveArea : 1;

    if (soils.length === 0) return null;

    const soilMeta = soils
        .filter((soil) => !budget.useFarmSoils || farmSoilBlocks.find((fsb) => fsb.soilId === soil.id))
        .map((soil, i) => {
            const soilFarmSoilBlocks = farmSoilBlocks.filter((fsb) => fsb.soilId === soil.id);
            const soilBlockIds = soilFarmSoilBlocks.map((fsb) => fsb.blockId);
            const soilBlocks = blocks.filter((blk) => soilBlockIds.includes(blk.id)).map((b) => ({ ...b, percentageOfBlock: soilFarmSoilBlocks.find((fsb) => fsb.soilId === soil.id && fsb.blockId === b.id).percentageOfBlock }));
            const soilTotalArea = soilFarmSoilBlocks.reduce((t, fsb) => (t += fsb.area), 0);
            const percentageOfProductiveBlocks = utils.round((soilTotalArea / totalProductiveArea) * 100, 1);
            return { soil, soilBlocks, soilTotalArea, percentageOfProductiveBlocks };
        });

    soilMeta.sort((a, b) => b.percentageOfProductiveBlocks - a.percentageOfProductiveBlocks);

    const blocksWithNoSoil = productiveBlocks.filter((b) => !farmSoilBlocks.some((fsb) => fsb.blockId === b.id));
    const noSoils = {
        blocks: blocksWithNoSoil,
        blocksTotalArea: blocksWithNoSoil.length > 0 ? totalProductiveArea - soilMeta.reduce((total, meta) => (total += meta.soilTotalArea), 0) : 0,
        percentageOfProductive: blocksWithNoSoil.length > 0 ? 100 - soilMeta.reduce((total, meta) => (total += meta.percentageOfProductiveBlocks), 0) : 0,
    };

    const deleteSoil = (soil) => {
        const confirmDelete = (soil) => {
            budget.soils = budget.soils.filter((s) => s.id !== soil.id);
            budget.farmSoilBlocks = budget.farmSoilBlocks.filter((fsb) => fsb.soilId !== soil.id);
            dispatch(updateBudget(budget));
        };
        dispatch(confirm(`Are you sure you want to delete this soil?`, () => confirmDelete(soil)));
    };

    const editSoil = (soil) => {
        const onSoilEdited = (soil) => {
            const { soils = [] } = budget;
            utils.merge(soil, soils);
            dispatch(updateBudget(budget));
        };

        const soilModalOptions = {
            analysis: budget,
            saveOnSubmit: true,
            onSubmit: onSoilEdited,
        };

        openSoilModal(soil, soilModalOptions);
    };

    const toggleSoilBlocks = (soilId) => {
        if (!expandedSoilIds.some((id) => id === soilId)) setExpandedSoilIds([...expandedSoilIds, soilId]);
        else setExpandedSoilIds(expandedSoilIds.filter((id) => id !== soilId));
    };

    //const confirmUseFarmSoils = () => {
    //    const useFarmSoils = () => {
    //        dispatch(updateBudget({ ...budget, useFarmSoils: !budget.useFarmSoils }));
    //    }
    //    const msg = budget.useFarmSoils ? `This will require you to manually assign soils to blocks. This is not the recommended approach. Are you sure you wish to do this?` : `This will replace all block soils with soils from the farm soil map based on block location (all blocks must be drawn). The existing manually assigned soils will be lost and cannot be recovered. Are you sure you want to do this?`
    //    dispatch(confirm(msg, useFarmSoils, true, ''));
    //}

    return (
        <Fragment>
            {soilModal}
            <TileSubPanel title="Soils" green stickyHeaderName="budget.soil.farmSoils.collapsed">
                <div className="FarmTable_wrapper">
                    <div className="FarmTable-supplementary u-pt-md">
                        <div className="Table">
                            <table>
                                <thead>
                                    <tr>
                                        <th className="th--shrink">Blocks</th>
                                        <th data-width="10">S-Map ref/Name</th>
                                        <th data-width="10">Group</th>
                                        <th data-width="10">Order</th>
                                        <th data-width="10">Drainage Class</th>
                                        <th data-width="15">Description</th>
                                        <th className="th--shrink u-textRight">Total area</th>
                                        <th className="th--shrink u-textRight">% of productive blocks</th>
                                        {!removeLinks && <th className="th--shrink"></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {soilMeta.map((sm, i) => {
                                        const { soil, soilBlocks, soilTotalArea } = sm;
                                        const description = soil.sibling && soil.sibling.description && soil.sibling.description.length > 0 ? soil.sibling.description : undefined;
                                        const soilProfile = soil && soil.soilGroup && utils.getSoilProfile(soil);
                                        const profileDrainageClass = soil.profileDrainageClass || soilProfile.profileDrainageClass;
                                        const drainageClass = profileDrainageClass ? utils.valueToText(refData.drainageClasses, profileDrainageClass) : "Default";
                                        const showBlocks = expandedSoilIds.some((b) => b === soil.id);
                                        return (
                                            <Fragment key={i}>
                                                <tr className={soilTotalArea === 0 ? " u-textError" : ""}>
                                                    <td className="u-textCenter">
                                                        <ActionLink onClick={() => toggleSoilBlocks(soil.id)}>
                                                            <i className={`icon icon-arrow-${showBlocks ? "up" : "down"}`}></i>
                                                        </ActionLink>
                                                    </td>
                                                    <td className="td--alignTop">
                                                        <div className="u-flexSplit">
                                                            <span>{(soil.sibling && soil.sibling.smapReference) || soil.name || "-"}</span>
                                                            {isSoilModified(soil) && (
                                                                <span className="IconLink--soil u-textWarning u-ml-md" title="Modified soil properties">
                                                                    Modified
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="td--alignTop">{utils.valueToText(refData.soilGroups, soil.soilGroup)}</td>
                                                    <td className="td--alignTop">{soil.soilOrder === "Undefined" ? "-" : soil.soilOrder}</td>
                                                    <td className="td--alignTop">{drainageClass}</td>
                                                    <td className="td--alignTop">{description}</td>
                                                    <td className="u-textRight td--alignTop">
                                                        {utils.round(soilTotalArea, 1)} <small>ha</small>
                                                    </td>
                                                    <td className="u-textRight td--alignTop">
                                                        {sm.percentageOfProductiveBlocks}
                                                        <small>%</small>
                                                    </td>
                                                    {!removeLinks && (
                                                        <td className="td--alignTop">
                                                            <div className="u-flexSplit">
                                                                <ActionLink className="IconLink--edit" id={`${soil.id}_edit`} onClick={() => editSoil(soil)} title="Edit">
                                                                    <span></span>
                                                                </ActionLink>
                                                                {soilBlocks.length === 0 && (
                                                                    <ActionLink className="IconLink--trash" id={`${soil.id}_remove`} onClick={() => deleteSoil(soil)} title="Delete">
                                                                        <span></span>
                                                                    </ActionLink>
                                                                )}
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                                {showBlocks && (
                                                    <tr className="tr-nested">
                                                        <td></td>
                                                        <td colSpan="8">
                                                            <b>Blocks using this soil</b>
                                                            {soilBlocks.length > 0 ? (
                                                                <ul className="disc">
                                                                    {soilBlocks.map((block) => (
                                                                        <li key={block.id}>
                                                                            <Link id={`soil_block_${block.id}`} to={`/app/farm/${farm.id}/analysis/${budget.id}/soil/blocks/${block.id}`} className="FarmTitle-name" title={block.name}>
                                                                                {block.name}
                                                                            </Link>
                                                                            <span>- {block.percentageOfBlock}% of block</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            ) : (
                                                                <div className="u-flex Alert Alert--info u-mt-0 u-mb-0">
                                                                    <i className="icon icon-info" />
                                                                    <div style={{ paddingTop: "3px" }}>
                                                                        <div>No blocks are currently using this soil.</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })}

                                    {blocksWithNoSoil.length > 0 && (
                                        <tr className="Table-tr">
                                            <td colSpan="4">
                                                <b>Blocks require soil data</b>
                                            </td>
                                            <td>{noSoils.blocks.reduce((allNames, block) => (allNames += allNames.length > 0 ? `, ${block.name}` : block.name), "")}</td>
                                            <td></td>
                                            <td className="u-textRight">
                                                {utils.round(noSoils.blocksTotalArea, 1)} <small>ha</small>
                                            </td>
                                            <td className="u-textRight">
                                                {utils.round(noSoils.percentageOfProductive, 1)}
                                                <small>%</small>
                                            </td>
                                            {!removeLinks && <td></td>}
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </TileSubPanel>
        </Fragment>
    );
}
